"use client";

import { Compass, Box, Leaf, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 02.1",
    icon: Compass,
    title: "Global",
    italicWord: "Reach",
    titleSuffix: "",
    description: "Leverage our expansive network of verified suppliers and buyers across international markets to secure the best trade opportunities."
  }, {
    index: "§ 02.2",
    icon: Box,
    title: "Verified",
    italicWord: "Quality",
    titleSuffix: "",
    description: "Every partner in our ecosystem undergoes rigorous vetting, ensuring that your supply chain remains reliable, transparent, and efficient."
  }, {
    index: "§ 02.3",
    icon: Leaf,
    title: "Smart",
    italicWord: "Logistics",
    titleSuffix: "",
    description: "Streamlined digital workflows that reduce friction, optimize shipping timelines, and provide real-time visibility into your trade operations."
  }];
  return <section data-section-id="2110" id="why-companies-work-with-ktex" className="relative overflow-hidden bg-white py-28 md:py-36 text-slate-900">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-sans)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #1a3a52",
      opacity: 0.06,
      fontWeight: 700
    }}>
        02
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-sans)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #1a3a52",
      opacity: 0.06,
      fontWeight: 700
    }}>
        02
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              <span className="text-[#d4a574]">§ 02</span>
              <span className="text-slate-300">—</span>
              <span className="text-slate-500">CORE ADVANTAGES / KTEX VALUE</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
                <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
                <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
              </div>
            </div>

            {}
            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "#1a3a52"
          }}>
              Trusted trade,{" "}
              <br className="hidden sm:block" />
              global{" "}
              <em style={{
              fontStyle: "italic",
              fontWeight: 400
            }}>
                excellence
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-slate-600" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.55
          }}>
              KTEX empowers businesses to scale through a secure, data-driven trading platform designed to simplify complex international supply chains.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-shadow duration-300 ease-out hover:shadow-lg bg-white border border-slate-100" style={{
              borderRadius: "16px"
            }}>
                  {}
                  <span aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-t border-l border-[#d4a574]" />
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-t border-r border-[#d4a574]" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-b border-l border-[#d4a574]" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-b border-r border-[#d4a574]" />

                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                lineHeight: 1
              }}>
                    <span className="text-[#d4a574]">{pillar.index}</span>
                    <span className="text-slate-200">—</span>
                    <span className="text-slate-400">ADVANTAGE</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:rotate-6 text-[#1a3a52]">
                    <Icon size={32} strokeWidth={1.5} />
                  </div>

                  {}
                  <h3 className="mt-7 font-default text-[#1a3a52]" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                fontWeight: 600,
                letterSpacing: "-0.012em",
                lineHeight: 1.15
              }}>
                    {pillar.title}{" "}
                    <em style={{
                  fontStyle: "italic",
                  fontWeight: 400
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  {}
                  <p className="font-default mt-5 text-slate-600" style={{
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0.005em",
                lineHeight: 1.65
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-100" />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
                      <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
                      <span className="h-1 w-1 rounded-lg bg-[#d4a574]" />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <button type="button" className="group/link relative inline-flex items-center gap-2 font-default text-[#1a3a52]" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600
                }}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full bg-[#1a3a52]" />
                      </span>
                      <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </button>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}